// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * Sections whose detail pages are siblings sharing one tab set, so the page and tab you're on
 * survive a jump from one entity to the next (searching a store from a store's Settings tab
 * lands on the new store's Settings tab, and from its Edit page on the new store's Edit page,
 * instead of dropping you back on Info).
 */
const CARRYOVER_SECTIONS = ["chains", "stores"] as const;

export type CarryoverSection = (typeof CARRYOVER_SECTIONS)[number];

/** `detail` is the read page (`/stores/x`), `edit` its edit page (`/stores/x/edit`). */
export type CarryoverView = "detail" | "edit";

/* `tab` is omitted on a view's default tab, whose URL carries no `t` param. */
export type Carryover = { section: CarryoverSection; view: CarryoverView; tab?: string };

/* The entity segment plus an optional `/edit` — `/stores/compare/a/b` is not a detail page. */
const DETAIL_PATH = new RegExp(`^/(${CARRYOVER_SECTIONS.join("|")})/([^/]+)(/edit)?/?$`);

/**
 * What a jump between siblings should carry, or undefined when there's nothing to carry:
 * we're not on a chain/store detail or edit page, or we're on the detail page's default (Info) tab.
 */
export function readCarryover(pathname: string, search: unknown): Carryover | undefined {
  const match = DETAIL_PATH.exec(pathname);
  if (!match) return undefined;
  const section = match[1] as CarryoverSection;
  const view: CarryoverView = match[3] ? "edit" : "detail";
  const rawTab = (search as { t?: unknown } | null | undefined)?.t;
  const tab = typeof rawTab === "string" && rawTab !== "" && rawTab !== "info" ? rawTab : undefined;
  if (view === "detail" && !tab) return undefined;
  return tab ? { section, view, tab } : { section, view };
}

/**
 * Where a jump to `name` in `section` lands — the carried page and tab only apply within their own
 * section; anything else opens the plain detail page.
 */
export function carriedDestination(carried: Carryover | undefined, section: CarryoverSection, name: string) {
  const base = `/${section}/${name}`;
  if (carried?.section !== section) return { to: base, search: undefined };
  return {
    to: carried.view === "edit" ? `${base}/edit` : base,
    search: carried.tab ? { t: carried.tab } : undefined,
  };
}
